/**
 * Ticket Z14: Gemeinsame Anfragenliste im Backend.
 *
 * Eine Liste für alle unverbindlichen Anfragen, filterbar nach Herkunft und
 * Status. Ticket Z12 (Produktkarten im Cockpit) erscheint automatisch hier,
 * sobald dort Anfragen mit Herkunft „Cockpit – Produktkarte" entstehen.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { listInquiries, updateInquiryStatus } from "@/lib/inquiries.functions";
import { warmAdminQuery } from "@/lib/admin-loader";

import { CONTACT_TOPIC_LABELS } from "@/lib/contact.shared";
import {
  INQUIRY_SOURCES,
  INQUIRY_SOURCE_LABELS,
  INQUIRY_NOTIFICATION_STATUS_LABELS,
  INQUIRY_STATUS_FILTER_LABELS,
  INQUIRY_STATUS_FILTERS,
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  type InquirySource,
  type InquiryStatus,
  type InquiryStatusFilter,
} from "@/lib/inquiries";


export const Route = createFileRoute("/_admin/admin/inquiries")({
  // Ticket AD4a: Sprungziel aus dem Arbeitsvorrat (?status=neu).
  validateSearch: (search: Record<string, unknown>): { status?: string } => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  loader: ({ context }) => {
    // Standardansicht: Neu & In Bearbeitung schon während des Seitenwechsels laden.
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin-inquiries", "", "neu_und_in_bearbeitung"],
      queryFn: () =>
        listInquiries({ data: { source: null, status: "neu_und_in_bearbeitung" } }),
    });
  },
  component: InquiriesPage,
});


const selectClass =
  "rounded-sm border border-foreground/20 bg-background px-3 py-2 font-body text-sm text-foreground focus:border-primary focus:outline-none";

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Berlin",
  }).format(d);
}

/** Klartext des Anliegens aus dem Kontaktformular. */
function topicLabel(value: string): string {
  return (
    (CONTACT_TOPIC_LABELS as Record<string, string | undefined>)[value] ?? value
  );
}


function InquiriesPage() {
  const routeSearch = Route.useSearch();
  const listFn = useServerFn(listInquiries);
  const updateFn = useServerFn(updateInquiryStatus);
  const queryClient = useQueryClient();

  const [source, setSource] = useState<InquirySource | "">("");
  const [status, setStatus] = useState<InquiryStatusFilter>(
    (INQUIRY_STATUSES as readonly string[]).includes(routeSearch.status ?? "")
      ? (routeSearch.status as InquiryStatus)
      : "neu_und_in_bearbeitung",
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-inquiries", source, status],
    queryFn: () =>
      listFn({
        data: {
          source: source || null,
          status: status === "" ? null : status,
        },
      }),
  });

  const update = useMutation({
    mutationFn: async (vars: { id: string; status: InquiryStatus }) =>
      updateFn({ data: { id: vars.id, status: vars.status } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-inquiries"] });
      toast.success("Status aktualisiert.");
    },
    onError: (e: Error) => toast.error(e.message || "Aktualisierung fehlgeschlagen."),
  });

  return (
    <div className="space-y-8 p-6 md:p-8">
      <header className="space-y-2">
        <h1 className="font-display text-2xl font-extrabold text-primary">Anfragen</h1>
        <p className="max-w-[70ch] font-body text-sm text-muted-foreground">
          Alle unverbindlichen Anfragen an einer Stelle – vom Kontaktformular, von der
          Weiterbildungsseite und aus dem Kampagnencockpit. Über die Filter blenden Sie
          einzelne Herkünfte oder Status aus.

        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        <select
          aria-label="Herkunft"
          className={selectClass}
          value={source}
          onChange={(e) => setSource(e.target.value as InquirySource | "")}
        >
          <option value="">Alle Herkünfte</option>
          {INQUIRY_SOURCES.map((s) => (
            <option key={s} value={s}>
              {INQUIRY_SOURCE_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          className={selectClass}
          value={status}
          onChange={(e) => setStatus(e.target.value as InquiryStatusFilter)}
        >
          {INQUIRY_STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {INQUIRY_STATUS_FILTER_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <p className="flex items-center gap-2 font-body text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Wird geladen …
        </p>
      )}
      {isError && (
        <p className="font-body text-sm text-destructive">
          {(error as Error)?.message ?? "Anfragen konnten nicht geladen werden."}
        </p>
      )}

      {data && data.length === 0 && (
        <div className="flex items-center gap-3 rounded-sm border border-foreground/15 p-6 font-body text-sm text-muted-foreground">
          <Inbox className="h-5 w-5 shrink-0" aria-hidden />
          Noch keine Anfragen vorhanden.
        </div>
      )}

      {data && data.length > 0 && (
        <ul className="space-y-4">
          {data.map((row) => (
            <li key={row.id} className="rounded-sm border border-foreground/15 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-display text-base font-extrabold text-foreground">
                    {row.name}
                    {row.organisation ? ` · ${row.organisation}` : ""}
                  </p>
                  <p className="mt-1 font-body text-sm text-muted-foreground">
                    {INQUIRY_SOURCE_LABELS[row.source]} · {formatDate(row.createdAt)}
                    {row.clientName ? ` · Kunde: ${row.clientName}` : ""}
                  </p>
                  {row.topic && (
                    <p className="mt-1 font-body text-sm text-foreground">
                      Anliegen: {topicLabel(row.topic)}
                    </p>
                  )}
                  {/* Ticket ME1: erkennbar, von welcher Mentoring-Seite die
                      Anfrage kam. */}
                  {row.mentoringTitle && (
                    <p className="mt-1 font-body text-sm text-foreground">
                      Mentoring: {row.mentoringTitle}
                    </p>
                  )}
                  <p className="mt-2 font-body text-sm text-foreground">
                    <a
                      href={`mailto:${row.email}`}
                      className="underline underline-offset-2 hover:opacity-80"
                    >
                      {row.email}
                    </a>
                    {row.phone ? ` · ${row.phone}` : ""}
                  </p>
                  <p className="mt-2 flex flex-wrap items-center gap-2 font-body text-xs text-muted-foreground">
                    <span
                      className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 ${
                        row.notificationStatus === "failed"
                          ? "bg-destructive/10 text-destructive"
                          : row.notificationStatus === "sent"
                            ? "bg-primary/10 text-primary"
                            : "bg-foreground/5 text-muted-foreground"
                      }`}
                    >
                      {row.notificationStatus
                        ? INQUIRY_NOTIFICATION_STATUS_LABELS[row.notificationStatus]
                        : "Benachrichtigung ohne Vermerk"}
                    </span>
                    {row.consentAt && (
                      <span>Datenschutz bestätigt: {formatDate(row.consentAt)}</span>
                    )}
                  </p>

                </div>
                <select
                  aria-label={`Status von ${row.name}`}
                  className={selectClass}
                  value={row.status}
                  disabled={update.isPending}
                  onChange={(e) =>
                    update.mutate({ id: row.id, status: e.target.value as InquiryStatus })
                  }
                >
                  {INQUIRY_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {INQUIRY_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>

              {row.serviceTitles.length > 0 && (
                <div className="mt-4">
                  <p className="font-display text-sm font-extrabold text-foreground">
                    Interesse an
                  </p>
                  <ul className="mt-1 list-disc pl-5 font-body text-sm text-muted-foreground">
                    {row.serviceTitles.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {row.message && (
                <p className="mt-4 whitespace-pre-wrap font-body text-sm text-foreground">
                  {row.message}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
